import React, { useEffect, useState, useCallback } from "react";

import Masonry from "@mui/lab/Masonry";
import { Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useOperationDespatchContext } from "../../../../../../../contexts/DespatchContext";
import { useAppState } from "Context/StateContext";
import { MemberCertificationStatus } from "Models/member";
import { MemberCertification } from "Models/members/member.certification";
import { useTranslation } from "react-i18next";
import { ICategory } from "Types";
import { capitalize } from "Util/capitalize";
import { checkMemberAvailability, checkResourceAvailability } from "Util/checkAvailability";
import { ViewPerspective, CertificationType, ResourceSubGroup, ResourceName } from "Util/enums";
import { isShowAction, operationsPermissions } from "Util/permissions";

import { ResourceEquipmentCard } from "../../ResourceList/components/ResourceEquipmentCard";
import { ResourceMemberCard } from "../../ResourceList/components/ResourceMemberCard";
import { ResourceOrderCard } from "../../ResourceList/components/ResourceOrderCard";
import { ResourceStorageBoxCard } from "../../ResourceList/components/ResourceStorageBoxCard";
import { ResourceTeamCard } from "../../ResourceList/components/ResourceTeamCard";
import { ResourceList } from "../../ResourceList/index";
import { ResourceSectionList } from "../../ResourceSectionList";
import { ResourceViewerLayout } from "../../ResourceViewerLayout";
import { DNDFieldsNames, DNDSourceTypes } from "../useDragSystem/slotTypes";
import { useDragSystem } from "../useDragSystem/useDragSystem";
import { ResourceViewerEmptyState } from "../ViewerEmptyState";
import { TeamCard } from "./TeamCard/index";
import { TeamCreateModal } from "./TeamCreateModal/TeamCreateModal";
import { hasTaskTimezoneIssues } from "../../../../../../../util/timezone.issue";

type Props = {
  dataPermission: {
    members: boolean;
    teams: boolean;
    tasks: boolean;
    equipments: boolean;
    storageBoxes: boolean;
  };
};

const memberTypes = (memberCertifications?: MemberCertification[]) => {
  const typesSet = new Set<CertificationType>();

  memberCertifications?.forEach((certification) => {
    if (certification.status === MemberCertificationStatus.APPROVED) {
      typesSet.add(certification.type);
    }
  });

  return Array.from(typesSet);
};

export const ResourceTeamViewer = (props: Props) => {
  const { dataPermission } = props;
  const { currentUser, formatWithUserTimezone, userTimezone } = useAppState();

  const {
    members,
    membersCount,
    isLoadingMembers,
    isLoadingNextMemberPage,
    hasNextMemberPage,
    fetchNextMemberPage,

    equipments,
    equipmentsCount,
    isLoadingEquipments,
    hasNextEquipmentPage,
    fetchNextEquipmentPage,
    isLoadingNextEquipmentPage,

    storageBoxes,
    storageBoxesCount,
    isLoadingStorageBoxes,
    hasNextStorageBoxPage,
    fetchNextStorageBoxPage,
    isLoadingNextStorageBoxPage,

    teams,
    teamsCount,
    isLoadingTeams,
    crewsFiltered,
    isLoadingCrews,
    isFetchingCrews,

    removeCrewFromBoard,
    usersInCrewWithTasks,
    usersInCrewWithCrewName,
    equipmentsInCrewWithTasks,
    equipmentsInCrewWithCrewName,
    addTeamToBoard,

    tasksAvailable: tasks,
    taskSearch,
    isFetchingTasks,
    taskSetSearch,

    currentDate: actualDate,
    setViewPerspective,
    activeFilters,
    setActiveFilters,
    setActiveSearches,
    totalItemsPerColumn,
  } = useOperationDespatchContext();
  const [isTaskCreateModalOpen, setIsTaskCreateModalOpen] = useState(false);

  const { handleDrop, removeElementFromDespatch, handleDragStart } = useDragSystem();

  const theme = useTheme();
  const lgAndUp = useMediaQuery(theme.breakpoints.up("lg"));
  const xlAndUp = useMediaQuery(theme.breakpoints.up("xl"));

  const isUserEnableToEdit = operationsPermissions({
    subGroup: ResourceSubGroup.DISPATCH,
    resourceName: ResourceName.DISPATCH,
    currentUser,
  })?.edit;

  const { t: translate } = useTranslation();

  const handleCloseModal = () => setIsTaskCreateModalOpen(false);

  useEffect(() => {
    setViewPerspective(ViewPerspective.TEAM);
  }, []);

  const membersCategories: ICategory[] = [
    {
      name: translate("belongsTo"),
      filterKey: "belongsTo",
      options: [
        { label: translate("own"), value: "own" },
        { label: translate("subcontractor"), value: "subcontractor" },
      ],
    },
    {
      name: translate("typeOfUser"),
      filterKey: "typeOfUser",
      options: [
        { label: translate("leader"), value: "leader" },
        { label: translate("staff"), value: "staff" },
      ],
    },
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" }, // I know it's shitty but the type check of this model is designed to receive only strings
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const teamCategories: ICategory[] = [
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" },
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const equipmentCategories: ICategory[] = [
    {
      name: translate("belongsTo"),
      filterKey: "belongsTo",
      options: [
        { label: translate("own"), value: "own" },
        { label: translate("subcontractor"), value: "subcontractor" },
      ],
    },
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" },
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const storageBoxCategories: ICategory[] = [
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" },
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const returnCheckMemberAvailability = useCallback(
    (member) => {
      const isAvailable = !checkMemberAvailability(actualDate, member, userTimezone.name)
        .isAvailable;
      return isAvailable;
    },
    [actualDate]
  );

  const renderLeftSection = () => {
    return (
      <>
        {isShowAction(currentUser, dataPermission.members) && (
          <ResourceSectionList
            title={translate("members")}
            defaultExpanded
            itemsQuantity={membersCount}
            isLoading={isLoadingMembers}
            loadMore={
              hasNextMemberPage && members.length
                ? { isLoading: isLoadingNextMemberPage, action: fetchNextMemberPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, memberSearch: searchValue }));
            }}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, memberFilters: filters }));
                },
                categories: membersCategories,
              },
            }}
            isActiveFilterByOptions={true}
            activeFilters={activeFilters.memberFilters}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                memberFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_MEMBERS}
              indexPrefix={100000}
              isLoading={isLoadingMembers}
              emptyMessage={translate("noMemberFound")}
              items={members.map((member) => {
                return {
                  id: Number(member.id),
                  sourceType: member?.userCompany?.leader
                    ? DNDSourceTypes.CREW_LEADER
                    : DNDSourceTypes.CREW_MEMBER,
                  isDisabled: returnCheckMemberAvailability(member),
                  payload: {
                    iconName: member?.userCompany?.leader ? ("crown" as const) : ("user" as const),
                    name: member.fullName!,
                    tasks: usersInCrewWithTasks[member.id!],
                    crewNamesRelated: usersInCrewWithCrewName[member.id!] || [],
                    isSubcontractor: !!member.subcontractorId,
                    availability: member.availabilities,
                    certificationsType: memberTypes(member.certifications),
                  },
                };
              })}
              isDisabled={!isUserEnableToEdit}
              render={(item) => (
                <ResourceMemberCard
                  iconName={item.payload.iconName}
                  name={String(item.payload.name)}
                  tasks={item.payload.tasks}
                  crewNamesRelated={item.payload.crewNamesRelated}
                  isSubcontractor={item.payload.isSubcontractor}
                  availability={item.payload.availability}
                  actualDate={actualDate}
                  certificationsType={item.payload.certificationsType}
                />
              )}
            />
          </ResourceSectionList>
        )}

        {isShowAction(currentUser, dataPermission.teams) && (
          <ResourceSectionList
            title={translate("teams")}
            itemsQuantity={teamsCount}
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, teamSearch: searchValue }));
            }}
            isLoading={isLoadingTeams}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, teamsFilters: filters }));
                },
                categories: teamCategories,
              },
            }}
            isActiveFilterByOptions={true}
            activeFilters={activeFilters.teamsFilters}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                teamsFiltersCount: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_TEAMS}
              indexPrefix={200000}
              isDisabled
              isLoading={isLoadingTeams}
              emptyMessage={translate("noTeamFound")}
              items={teams.map((team) => ({
                id: Number(team.id),
                sourceType: DNDSourceTypes.CREW_TEAM,
                payload: {
                  name: team.name,
                  leader: team.memberLeader,
                  members: team.members,
                  equipments: team.fleets,
                  team,
                },
              }))}
              render={(item) => (
                <ResourceTeamCard
                  name={item.payload.name}
                  teamLeader={item.payload.leader}
                  members={item.payload.members}
                  equipments={item.payload.equipments}
                  team={item.payload.team}
                  onAddTeamToBoard={addTeamToBoard}
                  hasAddTeamToBoardButton={isUserEnableToEdit}
                />
              )}
            />
          </ResourceSectionList>
        )}
      </>
    );
  };

  const renderRightSection = () => {
    return (
      <>
        {isShowAction(currentUser, dataPermission.tasks) && (
          <ResourceSectionList
            title={capitalize(translate("tasks"))}
            itemsQuantity={tasks.length}
            handleSearchChange={taskSetSearch}
            isLoading={isFetchingTasks}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_ORDERS}
              indexPrefix={400000}
              isLoading={isFetchingTasks}
              emptyMessage={
                taskSearch.trim() ? translate("noOrderFound") : translate("noOrderAvailable")
              }
              isDisabled={!isUserEnableToEdit}
              items={tasks.map((task) => ({
                id: Number(task.id),
                sourceType: DNDSourceTypes.CREW_ORDER,
                isDisabled: !!hasTaskTimezoneIssues(
                  formatWithUserTimezone,
                  task,
                  currentUser?.preferences
                ),
                payload: {
                  task,
                },
              }))}
              render={(item) => {
                return (
                  <ResourceOrderCard
                    task={item.payload.task}
                    disabledTime={!isUserEnableToEdit}
                    isInResourceSection={true}
                  />
                );
              }}
            />
          </ResourceSectionList>
        )}

        {isShowAction(currentUser, dataPermission.equipments) && (
          <ResourceSectionList
            title={translate("equipments")}
            itemsQuantity={equipmentsCount}
            isLoading={isLoadingEquipments}
            loadMore={
              hasNextEquipmentPage
                ? { isLoading: isLoadingNextEquipmentPage, action: fetchNextEquipmentPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, equipmentSearch: searchValue }));
            }}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, equipmentsFilters: filters }));
                },
                categories: equipmentCategories,
              },
            }}
            activeFilters={activeFilters.equipmentsFilters}
            isActiveFilterByOptions={true}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                equipmentsFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_FLEETS}
              indexPrefix={300000}
              isLoading={isLoadingEquipments}
              emptyMessage={translate("noEquipmentFound")}
              isDisabled={!isUserEnableToEdit}
              items={equipments.map((equipment) => {
                return {
                  id: Number(equipment.id),
                  sourceType: DNDSourceTypes.CREW_EQUIPMENT,
                  isDisabled: !checkResourceAvailability(
                    actualDate,
                    equipment.availabilities,
                    userTimezone.name
                  ).isAvailable,
                  payload: {
                    tasks: equipmentsInCrewWithTasks[equipment.id!],
                    isSubcontractor: !!equipment.subcontractorId,
                    availability: equipment.availabilities,
                    equipment,
                    crewNamesRelated: equipmentsInCrewWithCrewName[equipment.id!] || [],
                  },
                };
              })}
              render={(item) => {
                return (
                  <ResourceEquipmentCard
                    actualDate={actualDate}
                    isSubcontractor={item.payload.isSubcontractor}
                    crewNamesRelated={item.payload.crewNamesRelated}
                    availabilities={item.payload.availability}
                    equipment={item.payload.equipment}
                    tasks={item.payload.tasks}
                  />
                );
              }}
            />
          </ResourceSectionList>
        )}

        {isShowAction(currentUser, dataPermission.storageBoxes) && (
          <ResourceSectionList
            title={translate("storageBoxes")}
            itemsQuantity={storageBoxesCount}
            isLoading={isLoadingStorageBoxes}
            loadMore={
              hasNextStorageBoxPage
                ? { isLoading: isLoadingNextStorageBoxPage, action: fetchNextStorageBoxPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, storageBoxSearch: searchValue }));
            }}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, storageBoxFilters: filters }));
                },
                categories: storageBoxCategories,
              },
            }}
            activeFilters={activeFilters.storageBoxFilters}
            isActiveFilterByOptions={true}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                storageBoxFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_STORAGE_BOXES}
              indexPrefix={400000}
              isLoading={isLoadingStorageBoxes}
              emptyMessage={translate("noStorageBoxFound")}
              isDisabled={!isUserEnableToEdit}
              items={storageBoxes.map((storageBox) => ({
                id: Number(storageBox.id),
                sourceType: DNDSourceTypes.CREW_STORAGE_BOX,
                payload: {
                  title: storageBox.storageBoxType?.name,
                  text: `#${storageBox.number}`,
                  capacityM3: storageBox.storageBoxType?.capacity,
                  isSingleUse: !!storageBox.singleUse,
                },
              }))}
              render={(item) => {
                return (
                  <ResourceStorageBoxCard
                    capacityM3={item.payload.capacityM3}
                    isSingleUse={!!item.payload.isSingleUse}
                    title={item.payload.title}
                    text={item.payload.text}
                  />
                );
              }}
            />
          </ResourceSectionList>
        )}
      </>
    );
  };

  const getLoadingItems = () => {
    let lenghtSize: number = 3;

    if (xlAndUp) lenghtSize = 6;
    else if (lgAndUp) lenghtSize = 4;

    return Array.from({ length: lenghtSize }).map((_element, index) => {
      return (
        <div key={index}>
          <Skeleton variant="rectangular" sx={{ borderRadius: "12px", flex: 1 }} height={384} />
        </div>
      );
    });
  };

  const getItems = () => {
    return crewsFiltered.map((crew) => {
      return (
        <div key={crew.id!}>
          <TeamCard
            crew={crew}
            onRemoveElement={removeElementFromDespatch}
            onCloseCrew={removeCrewFromBoard}
          />
        </div>
      );
    });
  };

  const renderContent = () => {
    let items;

    if (isLoadingCrews || isFetchingTasks) {
      items = getLoadingItems();
    } else {
      items = getItems();
    }

    if (items.length || isLoadingCrews || isFetchingTasks) {
      return (
        <Masonry
          columns={totalItemsPerColumn}
          spacing={2}
          sequential
          sx={{ margin: "0px !important" }}
        >
          {items}
        </Masonry>
      );
    }

    return (
      <ResourceViewerEmptyState
        title={translate("noTeamsAddedYet")}
        subtitle={
          isFetchingCrews
            ? translate("weAreDoingOneLastSearchWhatAboutACoffee")
            : translate("allCaughtUpWhatAboutACoffee")
        }
      />
    );
  };

  return (
    <ResourceViewerLayout
      leftSection={renderLeftSection()}
      rightSection={renderRightSection()}
      onDrop={handleDrop}
      onDragStart={handleDragStart}
    >
      {renderContent()}

      <TeamCreateModal open={isTaskCreateModalOpen} handleClose={handleCloseModal} />
    </ResourceViewerLayout>
  );
};
